import { useState } from 'react';
import {
  Plug,
  Plus,
  Loader2,
  DollarSign,
  FileText,
  X,
  Sparkles,
  Scale,
  Search,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useIntegrations } from '../hooks/useIntegrations';
import { IntegrationCard } from '../components/integrations/IntegrationCard';
import { IntegrationForm } from '../components/integrations/IntegrationForm';
import { StripeIntegrationCard } from '../components/integrations/StripeIntegrationCard';
import { StripeIntegrationForm } from '../components/integrations/StripeIntegrationForm';
import { LlmIntegrationCard } from '../components/integrations/LlmIntegrationCard';
import { LlmIntegrationForm } from '../components/integrations/LlmIntegrationForm';
import { SearchIntegrationCard } from '../components/integrations/SearchIntegrationCard';
import { SearchIntegrationForm } from '../components/integrations/SearchIntegrationForm';
import { useFinancialSettings } from '../hooks/useFinancial';
import { settingsService } from '../services/settings.service';

const INTEGRATION_CATALOG = [
  {
    type: 'DOCUGEN',
    label: 'DocuGen',
    description: 'Sincronize clientes e documentos do DocuGen como leads no CRM',
    icon: FileText,
    color: 'text-info bg-subtle',
  },
  {
    type: 'WISEJUS',
    label: 'WiseJus',
    description: 'Sincronize clientes e documentos do WiseJus como leads no CRM',
    icon: Scale,
    color: 'text-info bg-subtle',
  },
  {
    type: 'STRIPE',
    label: 'Stripe',
    description: 'Conecte pagamentos e assinaturas para o painel financeiro',
    icon: DollarSign,
    color: 'text-accent bg-subtle',
  },
  {
    type: 'OPENAI',
    label: 'Modelos de IA',
    description: 'Chaves da Anthropic e da OpenAI para análise de bounces e features futuras',
    icon: Sparkles,
    color: 'text-accent bg-subtle',
  },
  {
    type: 'SEARCH',
    label: 'Busca (Skool)',
    description: 'Chaves da Brave Search e da Serper para a prospecção de leads no Skool',
    icon: Search,
    color: 'text-accent bg-subtle',
  },
];

function IntegrationTypePicker({
  onSelect,
  onClose,
}: {
  onSelect: (type: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-ink-primary">Escolha o tipo</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-subtle">
            <X className="w-5 h-5 text-ink-tertiary" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {INTEGRATION_CATALOG.map((item) => (
            <button
              key={item.type}
              onClick={() => onSelect(item.type)}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-accent hover:bg-subtle transition-colors text-left"
            >
              <div className={`p-3 rounded-lg ${item.color}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-ink-primary">{item.label}</p>
                <p className="text-sm text-ink-tertiary">{item.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function IntegrationsPage() {
  const { data: integrations, isLoading } = useIntegrations();
  const { data: stripeSettings } = useFinancialSettings();
  const { data: aiSettings } = useQuery({
    queryKey: ['settings', 'ai'],
    queryFn: () => settingsService.getAi(),
  });
  const { data: searchSettings } = useQuery({
    queryKey: ['settings', 'search'],
    queryFn: () => settingsService.getSearch(),
  });
  const [showPicker, setShowPicker] = useState(false);
  const [showDocugenForm, setShowDocugenForm] = useState(false);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [showLlmForm, setShowLlmForm] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<any>(null);

  const stripeEverConfigured = !!stripeSettings?.data?.mcpServerCommand;
  const llmConfigured = !!(aiSettings?.hasAnthropicKey || aiSettings?.hasOpenaiKey);
  const searchConfigured = !!(searchSettings?.hasBraveKey || searchSettings?.hasSerperKey);
  const hasIntegrations =
    (integrations && integrations.length > 0) ||
    stripeEverConfigured ||
    llmConfigured ||
    searchConfigured;

  function handleEdit(integration: any) {
    setEditingIntegration(integration);
    setShowDocugenForm(true);
  }

  function handleTypeSelect(type: string) {
    setShowPicker(false);
    if (type === 'STRIPE') {
      setShowStripeForm(true);
    } else if (type === 'OPENAI') {
      setShowLlmForm(true);
    } else if (type === 'SEARCH') {
      setShowSearchForm(true);
    } else {
      setEditingIntegration(null);
      setShowDocugenForm(true);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Plug className="w-6 h-6 text-ink-primary" />
          <h1 className="text-2xl font-bold text-ink-primary">Integrações</h1>
        </div>
        <button
          onClick={() => setShowPicker(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-canvas bg-ink-primary rounded-lg hover:bg-ink-secondary"
        >
          <Plus className="w-4 h-4" />
          Nova Integração
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-ink-primary" />
        </div>
      ) : !hasIntegrations ? (
        <div className="text-center py-12 text-ink-tertiary">
          <Plug className="w-12 h-12 mx-auto mb-3 text-ink-tertiary" />
          <p className="text-lg font-medium">Nenhuma integração configurada</p>
          <p className="text-sm mt-1">Clique em &ldquo;Nova Integração&rdquo; para começar</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {stripeEverConfigured && <StripeIntegrationCard />}
          {llmConfigured && <LlmIntegrationCard />}
          {searchConfigured && <SearchIntegrationCard />}
          {integrations?.map((integration: any) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onEdit={() => handleEdit(integration)}
            />
          ))}
        </div>
      )}

      {showPicker && (
        <IntegrationTypePicker onSelect={handleTypeSelect} onClose={() => setShowPicker(false)} />
      )}

      {showDocugenForm && (
        <IntegrationForm
          integration={editingIntegration}
          onClose={() => {
            setShowDocugenForm(false);
            setEditingIntegration(null);
          }}
        />
      )}

      {showStripeForm && <StripeIntegrationForm onClose={() => setShowStripeForm(false)} />}

      {showLlmForm && <LlmIntegrationForm onClose={() => setShowLlmForm(false)} />}

      {showSearchForm && <SearchIntegrationForm onClose={() => setShowSearchForm(false)} />}
    </div>
  );
}
